import React, {FC, useMemo} from 'react';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../../../../libs/shared/core/src/helpers/internationalization/message';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {searchScopeDropdownItems} from '../../searchBar/queryBuilder/inboxesSearchQueryBuilderHelpers';
import {SearchHintClickTrackingOptions, trackSearchHintDropdownOpen} from '../../searchTrackingHelpers';
import {SearchHintItemPill} from '../searchHintItemPill';
import {SearchHintInDropdown} from './searchHintInDropdown';

interface SearchHintInItemProps {
  isDisabled: boolean;
  onClick: (
    modifier: ModifiersEnum,
    value: string,
    trackingOptions: Pick<SearchHintClickTrackingOptions, 'type'>,
  ) => void;
  queryHash?: string;
  onClear: (modifier: ModifiersEnum, trackingOptions: Pick<SearchHintClickTrackingOptions, 'type'>) => void;
  query: string;
}

export const SearchHintIn: FC<SearchHintInItemProps> = (props) => {
  const {queryHash, query, isDisabled} = props;
  const interactionId = `search-hint-in`;

  const selectedValues = useMemo(() => {
    const split = query.split(' ');
    return split.filter((value) => value.startsWith('in:')).map((e) => e.replace('in:', ''));
  }, [query]);

  const localize = useLocalize();
  const isActive = selectedValues.length > 0;

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <SearchHintItemPill
          isActive={isActive}
          isDisabled={isDisabled}
          isHovered={isOpen}
          interactionId={interactionId}
        >
          {renderSearchInTitle(selectedValues, localize)}
        </SearchHintItemPill>
      )}
      onPopoverToggle={(isOpen) => {
        if (isOpen) {
          trackSearchHintDropdownOpen({
            queryHash,
            modifier: ModifiersEnum.IN,
          });
        }
      }}
      renderModal={(popoverProps) => (
        <SearchHintInDropdown
          popoverProps={popoverProps}
          onClick={props.onClick}
          onClear={props.onClear}
          selectedValues={selectedValues}
        />
      )}
    />
  );
};

export const renderSearchInTitle = (selectedValues: Array<string>, localize: Localizer) => {
  const localizedIn = localize(
    defineMessage({
      id: 'search-hint-in',
      message: 'In',
    }),
  );
  if (selectedValues.length === 0) {
    return localizedIn;
  }
  const allValues = searchScopeDropdownItems();

  return `${localizedIn} ${allValues.find((e) => e.inputValue.value === selectedValues[0])?.textLabel}${
    selectedValues.length > 1 ? `, +${selectedValues.length - 1}` : ''
  }`;
};
